import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CloudUpload,
  Download,
  Delete,
  Visibility,
  Close,
  PictureAsPdf,
  Image as ImageIcon,
  Description,
  FolderOpen,
  InsertDriveFile,
} from "@mui/icons-material";
import { enqueueSnackbar } from "notistack";
import { apiUrl } from "./LoginSignup";
import "./BookingDocumentsModal.css";

const CATEGORIES = [
  "Client Document",
  "Result Screenshot",
  "Work Deliverable",
  "General Attachment",
];

const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024; // 25 MB

const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getCategoryClass = (category) => {
  switch (category) {
    case "Client Document":
      return "bdocs-cat-client";
    case "Result Screenshot":
      return "bdocs-cat-result";
    case "Work Deliverable":
      return "bdocs-cat-deliverable";
    default:
      return "bdocs-cat-general";
  }
};

const BookingDocumentsModal = ({
  bookingId,
  booking,
  open,
  onClose,
  onDocumentCountChanged,
}) => {
  const userSession = useMemo(
    () => JSON.parse(localStorage.getItem("userSession") || "{}"),
    []
  );

  const authHeaders = useMemo(
    () => ({
      authorization: userSession.token,
      "user-role": userSession.user_role,
    }),
    [userSession.token, userSession.user_role]
  );

  const fileInputRef = useRef(null);

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [category, setCategory] = useState(
    userSession.user_role === "bdm" ? "Client Document" : "Result Screenshot"
  );
  const [description, setDescription] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [isDragOver, setIsDragOver] = useState(false);

  // Preview Dialog state
  const [previewDoc, setPreviewDoc] = useState(null);

  // Delete Dialog state
  const [deleteConfirmDoc, setDeleteConfirmDoc] = useState(null);

  // Downloading indicator
  const [downloadingDocId, setDownloadingDocId] = useState(null);

  // Stable ref for parent callback to avoid re-render cycles
  const onDocCountChangedRef = useRef(onDocumentCountChanged);
  useEffect(() => {
    onDocCountChangedRef.current = onDocumentCountChanged;
  }, [onDocumentCountChanged]);

  const fetchDocuments = useCallback(async (showLoader = false) => {
    if (!bookingId) return;
    try {
      if (showLoader) setLoading(true);
      const res = await axios.get(`${apiUrl}/booking/${bookingId}/documents`, {
        headers: authHeaders,
      });
      const docs = res.data?.documents || [];
      setDocuments(docs);
      if (onDocCountChangedRef.current) {
        onDocCountChangedRef.current(bookingId, docs.length);
      }
    } catch (err) {
      console.warn("Fetch documents error:", err.message);
      enqueueSnackbar(
        err.response?.data?.message || "Failed to load attached documents.",
        { variant: "error" }
      );
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [bookingId, authHeaders]);

  useEffect(() => {
    if (open && bookingId) {
      fetchDocuments(true);
      setSelectedFiles([]);
      setDescription("");
      setActiveTab("all");
    }
  }, [open, bookingId, fetchDocuments]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    addValidFiles(files);
  };

  const addValidFiles = (files) => {
    const valid = [];
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        enqueueSnackbar(
          `"${file.name}" exceeds 25 MB. Maximum allowed size is 25 MB.`,
          { variant: "warning" }
        );
        continue;
      }
      valid.push(file);
    }
    setSelectedFiles((prev) => [...prev, ...valid].slice(0, 10));
  };

  const handleRemoveSelectedFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files || []);
    addValidFiles(files);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;
    try {
      setUploading(true);
      const formData = new FormData();
      selectedFiles.forEach((file) => {
        formData.append("files", file);
      });
      formData.append("category", category);
      formData.append("description", description);

      const res = await axios.post(
        `${apiUrl}/booking/${bookingId}/documents`,
        formData,
        {
          headers: {
            ...authHeaders,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      enqueueSnackbar(res.data?.message || "Documents uploaded successfully!", {
        variant: "success",
      });
      setSelectedFiles([]);
      setDescription("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      await fetchDocuments(false);
    } catch (err) {
      console.error("Upload error:", err);
      enqueueSnackbar(
        err.response?.data?.message || "Failed to upload documents.",
        { variant: "error" }
      );
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirmDoc) return;
    try {
      await axios.delete(
        `${apiUrl}/booking/${bookingId}/documents/${deleteConfirmDoc._id}`,
        { headers: authHeaders }
      );
      enqueueSnackbar("Document removed successfully.", { variant: "info" });
      setDeleteConfirmDoc(null);
      await fetchDocuments(false);
    } catch (err) {
      enqueueSnackbar(
        err.response?.data?.message || "Failed to delete document.",
        { variant: "error" }
      );
    }
  };

  const handleView = (doc) => {
    if (isImageFile(doc)) {
      setPreviewDoc(doc);
    } else {
      const token = userSession?.token || "";
      const viewUrl = `${apiUrl}/booking/${bookingId}/documents/${doc._id}/view?token=${encodeURIComponent(token)}`;
      window.open(viewUrl, "_blank");
    }
  };

  const handleDownload = async (doc) => {
    try {
      setDownloadingDocId(doc._id);
      const downloadUrl = `${apiUrl}/booking/${bookingId}/documents/${doc._id}/download`;

      // Download binary stream via axios with Authorization header
      const res = await axios.get(downloadUrl, {
        headers: authHeaders,
        responseType: "blob",
      });

      const blob = new Blob([res.data], {
        type: doc.fileType || res.headers["content-type"] || "application/octet-stream",
      });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", doc.fileName || "document");
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
      enqueueSnackbar(`"${doc.fileName}" downloaded successfully.`, {
        variant: "success",
      });
    } catch (err) {
      console.warn("Axios download failed, attempting authenticated browser fallback:", err.message);
      const token = userSession?.token || "";
      const fallbackUrl = `${apiUrl}/booking/${bookingId}/documents/${doc._id}/download?token=${encodeURIComponent(token)}`;
      window.open(fallbackUrl, "_blank");
    } finally {
      setDownloadingDocId(null);
    }
  };

  const filteredDocs = useMemo(() => {
    if (activeTab === "all") return documents;
    return documents.filter((d) => d.category === activeTab);
  }, [documents, activeTab]);

  const canDeleteDoc = (doc) => {
    const role = userSession?.user_role || "";
    const isManager = ["srdev", "senior admin", "admin", "dev"].includes(role);
    const isOwner = String(doc.uploadedBy) === String(userSession?.user_id);
    return isManager || isOwner;
  };

  const isImageFile = (doc) => {
    const ext = (doc.fileName || "").split(".").pop().toLowerCase();
    return (
      (doc.fileType || "").startsWith("image/") ||
      ["jpg", "jpeg", "png", "webp", "gif", "bmp"].includes(ext)
    );
  };

  const isPdfFile = (doc) => {
    const ext = (doc.fileName || "").split(".").pop().toLowerCase();
    return (doc.fileType || "") === "application/pdf" || ext === "pdf";
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        className="bdocs-dialog"
      >
        {/* Header */}
        <Box className="bdocs-header-box">
          <Box>
            <Box className="bdocs-header-title">
              <Box className="bdocs-icon-bubble">
                <FolderOpen fontSize="medium" />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Booking Documents & Attachments
                </Typography>
                <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                  Cloudinary Document Storage & Result Screenshots
                </Typography>
              </Box>
            </Box>
            <Box className="bdocs-booking-meta">
              <span className="bdocs-meta-chip">
                Company: <strong>{booking?.company_name || "—"}</strong>
              </span>
              <span className="bdocs-meta-chip">
                Client: <strong>{booking?.contact_person || "—"}</strong>
              </span>
              <span className="bdocs-meta-chip">
                BDM: <strong>{booking?.bdm || "—"}</strong>
              </span>
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "#ffffff" }} size="small">
            <Close />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 2.5 }}>
          {/* Upload Dropzone & Form */}
          <Box
            className={`bdocs-upload-section ${isDragOver ? "dragover" : ""}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              type="file"
              multiple
              ref={fileInputRef}
              onChange={handleFileSelect}
              style={{ display: "none" }}
              accept=".jpg,.jpeg,.png,.webp,.gif,.bmp,.svg,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
            />

            <Box
              className="bdocs-dropzone"
              onClick={() => fileInputRef.current?.click()}
            >
              <CloudUpload sx={{ fontSize: 40, color: "#2563eb", mb: 1 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b" }}>
                Click to browse or drag and drop files here
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Supports Images, PDFs, Word, and Excel documents (up to 25 MB per file)
              </Typography>
            </Box>

            {/* Selected Files Preview List */}
            {selectedFiles.length > 0 && (
              <Box className="bdocs-selected-files-list">
                <Typography variant="caption" sx={{ fontWeight: 700, color: "#334155" }}>
                  Selected Files ({selectedFiles.length}):
                </Typography>
                {selectedFiles.map((file, idx) => (
                  <Box key={idx} className="bdocs-file-item">
                    <Box className="bdocs-file-item-left">
                      {file.type.startsWith("image/") ? (
                        <ImageIcon color="primary" fontSize="small" />
                      ) : file.type === "application/pdf" ? (
                        <PictureAsPdf color="error" fontSize="small" />
                      ) : (
                        <Description color="action" fontSize="small" />
                      )}
                      <span className="bdocs-file-name">{file.name}</span>
                      <span className="bdocs-file-size">({formatBytes(file.size)})</span>
                    </Box>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveSelectedFile(idx)}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                ))}

                {/* Upload Options */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    mt: 1.5,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Document Category</InputLabel>
                    <Select
                      label="Document Category"
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                    >
                      {CATEGORIES.map((cat) => (
                        <MenuItem key={cat} value={cat}>
                          {cat}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    placeholder="Optional description / notes"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    sx={{ flex: 1, minWidth: 200 }}
                  />
                  <Button
                    variant="contained"
                    startIcon={
                      uploading ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <CloudUpload />
                      )
                    }
                    disabled={uploading}
                    onClick={handleUpload}
                    sx={{
                      background: "linear-gradient(135deg, #1e3a8a, #2563eb)",
                      fontWeight: 600,
                      px: 3,
                    }}
                  >
                    {uploading ? "Uploading..." : `Upload (${selectedFiles.length})`}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>

          {/* Filter Bar */}
          <Box className="bdocs-filter-bar">
            <Tabs
              value={activeTab}
              onChange={(_, val) => setActiveTab(val)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ minHeight: 38 }}
            >
              <Tab label={`All (${documents.length})`} value="all" sx={{ py: 0.5, minHeight: 36 }} />
              <Tab
                label={`Client Docs (${
                  documents.filter((d) => d.category === "Client Document").length
                })`}
                value="Client Document"
                sx={{ py: 0.5, minHeight: 36 }}
              />
              <Tab
                label={`Result Screenshots (${
                  documents.filter((d) => d.category === "Result Screenshot").length
                })`}
                value="Result Screenshot"
                sx={{ py: 0.5, minHeight: 36 }}
              />
              <Tab
                label={`Work Deliverables (${
                  documents.filter((d) => d.category === "Work Deliverable").length
                })`}
                value="Work Deliverable"
                sx={{ py: 0.5, minHeight: 36 }}
              />
            </Tabs>
          </Box>

          {/* Documents Grid */}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : filteredDocs.length === 0 ? (
            <Box className="bdocs-empty-state">
              <InsertDriveFile sx={{ fontSize: 48, color: "#cbd5e1" }} />
              <Typography variant="body2">
                No documents in this section yet.
              </Typography>
              <Typography variant="caption">
                Attach client documents or completed result screenshots using the upload area above.
              </Typography>
            </Box>
          ) : (
            <Box className="bdocs-grid">
              {filteredDocs.map((doc) => {
                const isImg = isImageFile(doc);
                const isPdf = isPdfFile(doc);
                return (
                  <Box key={doc._id} className="bdocs-card">
                    <Box className="bdocs-card-top">
                      <Box
                        className="bdocs-thumb-box"
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleView(doc)}
                      >
                        {isImg ? (
                          <img
                            src={doc.fileUrl}
                            alt={doc.fileName}
                            className="bdocs-thumb-img"
                          />
                        ) : isPdf ? (
                          <PictureAsPdf sx={{ color: "#ef4444", fontSize: 28 }} />
                        ) : (
                          <Description sx={{ color: "#2563eb", fontSize: 28 }} />
                        )}
                      </Box>

                      <Box className="bdocs-card-info">
                        <Tooltip title={doc.fileName}>
                          <span className="bdocs-card-filename">
                            {doc.fileName}
                          </span>
                        </Tooltip>
                        <span
                          className={`bdocs-card-category ${getCategoryClass(
                            doc.category
                          )}`}
                        >
                          {doc.category || "Client Document"}
                        </span>
                      </Box>
                    </Box>

                    {/* Metadata */}
                    <Box className="bdocs-card-meta">
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <span>Size: {formatBytes(doc.fileSize)}</span>
                        <span>{formatDate(doc.uploadedAt)}</span>
                      </Box>
                      <span className="bdocs-uploader-pill">
                        Uploaded by: {doc.uploadedByName || "User"}{" "}
                        <Chip
                          size="small"
                          label={doc.uploadedByRole || "bdm"}
                          sx={{ height: 18, fontSize: "0.68rem" }}
                        />
                      </span>
                      {doc.description ? (
                        <span style={{ fontStyle: "italic", marginTop: 2 }}>
                          "{doc.description}"
                        </span>
                      ) : null}
                    </Box>

                    {/* Actions */}
                    <Box className="bdocs-card-actions">
                      <Tooltip title="Preview / Open">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleView(doc)}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title={downloadingDocId === doc._id ? "Downloading..." : "Download File"}>
                        <span>
                          <IconButton
                            size="small"
                            color="success"
                            disabled={downloadingDocId === doc._id}
                            onClick={() => handleDownload(doc)}
                          >
                            {downloadingDocId === doc._id ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <Download fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>

                      {canDeleteDoc(doc) && (
                        <Tooltip title="Delete Document">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteConfirmDoc(doc)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5, backgroundColor: "#f8fafc" }}>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            Total {documents.length} document(s) attached
          </Typography>
          <Button onClick={onClose} variant="outlined" size="small">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Modal */}
      {previewDoc && (
        <Dialog
          open={Boolean(previewDoc)}
          onClose={() => setPreviewDoc(null)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {previewDoc.fileName}
            </Typography>
            <IconButton onClick={() => setPreviewDoc(null)} size="small">
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 2, textAlign: "center" }}>
            <img
              src={previewDoc.fileUrl}
              alt={previewDoc.fileName}
              style={{
                maxWidth: "100%",
                maxHeight: "75vh",
                objectFit: "contain",
                borderRadius: 8,
              }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 1.5 }}>
            <Button
              startIcon={downloadingDocId === previewDoc._id ? <CircularProgress size={16} color="inherit" /> : <Download />}
              variant="contained"
              disabled={downloadingDocId === previewDoc._id}
              onClick={() => handleDownload(previewDoc)}
            >
              {downloadingDocId === previewDoc._id ? "Downloading..." : "Download"}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmDoc && (
        <Dialog
          open={Boolean(deleteConfirmDoc)}
          onClose={() => setDeleteConfirmDoc(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Delete color="error" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Delete Document?
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              Are you sure you want to delete{" "}
              <strong>{deleteConfirmDoc.fileName}</strong>? This action will permanently
              remove the file from Cloudinary.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setDeleteConfirmDoc(null)}>Cancel</Button>
            <Button variant="contained" color="error" onClick={handleDelete}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default BookingDocumentsModal;
